// src/components/agent/UI.js
// Branded primitives for agent CRM screens.
import React from 'react';

export const BURG = '#6B1E2E';
export const BURG_DK = '#4A1220';
export const GOLD = '#C9A84C';
export const GOLD_PALE = '#F7F0DC';
export const CREAM = '#FAF7F2';
export const WHITE = '#FFFFFF';
export const MID = '#4B5563';
export const LIGHT = '#9CA3AF';
export const BORDER = '#E5E7EB';
export const BORDER_STRONG = '#D1D5DB';

export const PageHeader = ({ eyebrow, title, sub, right }) => (
  <div style={ph.wrap}>
    <div style={{ minWidth: 0 }}>
      {eyebrow && <div style={ph.eyebrow}>{eyebrow}</div>}
      <h1 style={ph.title}>{title}</h1>
      {sub && <p style={ph.sub}>{sub}</p>}
    </div>
    {right && <div>{right}</div>}
  </div>
);

const ph = {
  wrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, marginBottom: 24, flexWrap: 'wrap' },
  eyebrow: { fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: GOLD, marginBottom: 8, fontFamily: "'Inter', sans-serif" },
  title: { fontFamily: "'Manrope', sans-serif", fontSize: 32, fontWeight: 800, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0 },
  sub: { fontSize: 15, color: MID, marginTop: 8, marginBottom: 0, lineHeight: 1.55, fontFamily: "'Inter', sans-serif" }
};

export const Card = ({ title, children, right, padded = true, style: extra }) => (
  <section style={{ ...c.card, ...(extra || {}) }}>
    {(title || right) && (
      <header style={c.head}>
        {title && <div style={c.headTitle}>{title}</div>}
        {right}
      </header>
    )}
    <div style={padded ? c.body : { padding: 0 }}>{children}</div>
  </section>
);

const c = {
  card: { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, marginBottom: 20, overflow: 'hidden', boxShadow: '0 1px 2px rgba(17,24,39,0.04)' },
  head: { padding: '16px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#FAFAFA' },
  headTitle: { fontFamily: "'Manrope', sans-serif", fontSize: 15, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' },
  body: { padding: 20 }
};

export const Btn = ({ variant = 'primary', children, ...rest }) => {
  const base = { borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", border: '1px solid transparent', transition: 'background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease', letterSpacing: '-0.005em', lineHeight: 1.2 };
  const variants = {
    primary: { background: BURG, color: WHITE, borderColor: BURG },
    gold:    { background: GOLD, color: BURG_DK, borderColor: GOLD },
    ghost:   { background: WHITE, color: '#1F2937', borderColor: BORDER_STRONG },
    danger:  { background: WHITE, color: '#B91C1C', borderColor: '#FCA5A5' }
  };
  return <button {...rest} style={{ ...base, ...(variants[variant] || variants.primary), ...(rest.style || {}) }}>{children}</button>;
};

export const Field = ({ label, children, hint, required }) => (
  <label style={f.wrap}>
    <span style={f.label}>{label}{required && <span style={f.req}>*</span>}</span>
    {children}
    {hint && <span style={f.hint}>{hint}</span>}
  </label>
);

export const Input = (props) => <input {...props} style={{ ...f.input, ...(props.style || {}) }} />;
export const Select = (props) => <select {...props} style={{ ...f.input, ...(props.style || {}) }} />;
export const TextArea = (props) => <textarea {...props} style={{ ...f.input, minHeight: 88, resize: 'vertical', ...(props.style || {}) }} />;

const f = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  label: { fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: "'Inter', sans-serif" },
  req: { color: BURG, marginLeft: 3 },
  hint: { fontSize: 12, color: LIGHT, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" },
  input: { padding: '10px 14px', border: `1px solid ${BORDER_STRONG}`, borderRadius: 8, fontSize: 15, fontFamily: "'Inter', sans-serif", background: WHITE, color: '#111827', outline: 'none', transition: 'border-color 0.15s ease, box-shadow 0.15s ease' }
};

export const Pill = ({ children, color = 'gold' }) => {
  const colors = {
    gold:   { bg: GOLD_PALE,             fg: '#7A5D1A' },
    burg:   { bg: 'rgba(107,30,46,0.08)', fg: BURG },
    green:  { bg: '#DCFCE7',             fg: '#166534' },
    red:    { bg: '#FEE2E2',             fg: '#991B1B' },
    gray:   { bg: '#F3F4F6',             fg: '#4B5563' },
    blue:   { bg: '#DBEAFE',             fg: '#1E40AF' }
  };
  const k = colors[color] || colors.gold;
  return <span style={{ display: 'inline-block', background: k.bg, color: k.fg, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, fontFamily: "'Inter', sans-serif", lineHeight: 1.4 }}>{children}</span>;
};

export const StatusPill = ({ status }) => {
  const map = {
    new: 'gold', contacted: 'blue', negotiating: 'blue',
    'under contract': 'green', assigned: 'green', closed: 'green',
    dead: 'gray', pending: 'gold', active: 'green'
  };
  return <Pill color={map[(status || '').toLowerCase()] || 'gray'}>{status || '—'}</Pill>;
};

export const EmptyState = ({ title, sub, action }) => (
  <div style={{ textAlign: 'center', padding: '56px 24px', color: MID }}>
    <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8, letterSpacing: '-0.01em' }}>{title}</div>
    {sub && <div style={{ fontSize: 14, color: LIGHT, marginBottom: 20, maxWidth: 420, margin: '0 auto 20px', lineHeight: 1.55, fontFamily: "'Inter', sans-serif" }}>{sub}</div>}
    {action}
  </div>
);
